import { useEffect, useState } from 'react';
import { aiAdminApi, incubationApi, marApi } from '../lib/api';

/** IA, accompagnement École des Banges, incubateurs et corpus académique. */
export function SupportAdminPage() {
  const [tab, setTab] = useState<'ai' | 'banges' | 'incubation' | 'mar'>('ai');
  const [signals, setSignals] = useState<any[]>([]);
  const [candidates, setCandidates] = useState<any[]>([]);
  const [centers, setCenters] = useState<any[]>([]);
  const [services, setServices] = useState<any[]>([]);
  const [fields, setFields] = useState<any[]>([]);
  const [degrees, setDegrees] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState<any>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => { reload(); }, []);

  async function reload() {
    for (const [fn, set] of [
      [aiAdminApi.signals, setSignals],
      [aiAdminApi.candidates, setCandidates],
      [incubationApi.centers, setCenters],
      [incubationApi.services, setServices],
      [marApi.fields, setFields],
      [marApi.degrees, setDegrees],
      [marApi.summary, setSummary],
    ] as any[]) {
      try { set(await fn()); } catch { /* droits ou données absentes */ }
    }
  }

  async function run(fn: () => Promise<any>, note: string) {
    setError(''); setNotice('');
    try { await fn(); setNotice(note); await reload(); }
    catch (e: any) { setError(String(e.message || e)); }
  }

  const ask = (l: string, d = '') => window.prompt(l, d) ?? '';

  return <section className="page">
    <header className="page-header">
      <div>
        <span className="eyebrow">ACCOMPAGNEMENT</span>
        <h1>IA, orientation et incubateurs</h1>
      </div>
    </header>

    <div className="tab-row">
      {([['ai', 'IA'], ['banges', 'Banges'], ['incubation', 'Incubateurs'], ['mar', 'Corpus']] as const).map(([k, l]) =>
        <button key={k} className={`button ${tab === k ? 'primary' : 'secondary'}`} onClick={() => setTab(k)}>{l}</button>)}
    </div>

    {error && <div className="alert error">{error}</div>}
    {notice && <div className="notice">{notice}</div>}

    {tab === 'ai' && <>
      <div className="card">
        <h3>Interroger le moteur</h3>
        <label>Question<input value={question} onChange={(e: any) => setQuestion(e.target.value)} /></label>
        <button className="button primary" disabled={!question.trim()} onClick={async () => {
          setError(''); setAnswer(null);
          try { setAnswer(await aiAdminApi.ask(question.trim())); }
          catch (e: any) { setError(String(e.message || e)); }
        }}>Demander</button>
        {answer && <pre className="json-block">{JSON.stringify(answer, null, 2).slice(0, 2500)}</pre>}
      </div>

      <div className="card">
        <h3>Signaux d'accompagnement ({signals.length})</h3>
        <button className="button secondary" onClick={() => {
          const learner = ask('Identifiant apprenant'); if (!learner) return;
          const type = ask('Type de signal', 'DIFFICULTY');
          const evidence = ask('Constat') || '—';
          run(() => aiAdminApi.createSignal({ learner_id: learner, signal_type: type, subject_ref: '', evidence }), 'Signal enregistré.');
        }}>Signaler un apprenant</button>
        {signals.slice(0, 15).map(s => <div key={s.id} className="order-row">
          <strong>{(s.learner_id || '').slice(0, 8)}</strong>
          <span>{s.signal_type}</span>
          <small>{s.evidence}</small>
        </div>)}
        {!signals.length && <p className="technical-note">Aucun signal.</p>}
      </div>
    </>}

    {tab === 'banges' && <div className="card">
      <h3>Candidats ({candidates.length})</h3>
      <button className="button secondary" onClick={() => {
        const learner = ask('Identifiant apprenant'); if (!learner) return;
        const evidence = ask('Motif') || '—';
        run(() => aiAdminApi.createCandidate({ learner_id: learner, subject_ref: '', evidence }), 'Candidat proposé.');
      }}>Proposer un candidat</button>
      {candidates.map(c => <div key={c.id} className="order-row">
        <strong>{(c.learner_id || '').slice(0, 8)}</strong>
        <span>{c.status}</span>
        <div className="toolbar">
          <button className="button secondary" onClick={() => run(() => aiAdminApi.reviewCandidate(c.id, 'APPROVED', ''), 'Candidat approuvé.')}>Approuver</button>
          <button className="button primary" onClick={() => run(() => aiAdminApi.activateCandidate(c.id), 'Candidat activé.')}>Activer</button>
        </div>
      </div>)}
      {!candidates.length && <p className="technical-note">Aucun candidat.</p>}
    </div>}

    {tab === 'incubation' && <div className="grid two">
      <div className="card">
        <h3>Centres ({centers.length})</h3>
        <button className="button secondary" onClick={() => {
          const inst = ask('Identifiant établissement'); if (!inst) return;
          const code = ask('Code du centre'); if (!code) return;
          run(() => incubationApi.createCenter({ institution_id: inst, code, name: ask('Nom') || code }), 'Centre créé.');
        }}>Créer un centre</button>
        {centers.map(c => <div key={c.id} className="order-row">
          <strong>{c.code} — {c.name}</strong>
          <button className="button secondary" onClick={() => {
            const owner = ask('Identifiant du porteur'); if (!owner) return;
            const title = ask('Intitulé du dossier') || 'Dossier';
            run(() => incubationApi.createCase(c.id, { owner_user_id: owner, title, summary: '' }), 'Dossier ouvert.');
          }}>Ouvrir un dossier</button>
        </div>)}
        {!centers.length && <p className="technical-note">Aucun centre.</p>}
      </div>
      <div className="card">
        <h3>Services externes ({services.length})</h3>
        <button className="button secondary" onClick={() => {
          const code = ask('Code'); if (!code) return;
          run(() => incubationApi.createService({ code, name: ask('Nom') || code, url: ask('URL') || 'https://' }), 'Service ajouté.');
        }}>Ajouter un service</button>
        {services.map(s => <div key={s.id} className="order-row">
          <strong>{s.code} — {s.name}</strong><span>{s.url}</span>
        </div>)}
        {!services.length && <p className="technical-note">Aucun service.</p>}
      </div>
    </div>}

    {tab === 'mar' && <div className="grid two">
      <div className="card">
        <h3>Corpus académique</h3>
        {summary && <pre className="json-block">{JSON.stringify(summary, null, 2).slice(0, 1200)}</pre>}
        <button className="button secondary" onClick={() => run(() => marApi.importCorpus(), 'Corpus importé.')}>Importer le corpus</button>
      </div>
      <div className="card">
        <h3>Domaines ({fields.length}) et diplômes ({degrees.length})</h3>
        <div className="toolbar">
          <button className="button secondary" onClick={() => {
            const code = ask('Code du domaine'); if (!code) return;
            run(() => marApi.createField({ code, title: ask('Intitulé') || code, description: '' }), 'Domaine créé.');
          }}>Ajouter un domaine</button>
          <button className="button secondary" onClick={() => {
            const code = ask('Code du diplôme'); if (!code) return;
            run(() => marApi.createDegree({ code, title: ask('Intitulé') || code, level_code: ask('Niveau', 'BACHELOR') || 'BACHELOR', minimum_credits: 120 }), 'Diplôme créé.');
          }}>Ajouter un diplôme</button>
        </div>
        {fields.slice(0, 10).map(f => <div key={f.id} className="order-row"><strong>{f.code}</strong><span>{f.title}</span></div>)}
      </div>
    </div>}
  </section>;
}
